import React, { useState, useEffect } from 'react';
import {
  Grid,
} from '@material-ui/core';
import PropTypes from 'prop-types';
import { map, findIndex, forEach, isObject, get } from 'lodash';

import {
  FormActionButtons,
  ErrorContainer,
  FormComboCheckBox,
  ExpandPanelContainer,
} from '../../../../components';
import {
  SAVE_AND_NEXT_TEXT,
} from '../labels';

export default function PolicyTemplates(props) {
  const {
    classes,
    errors,
    apiUuid,
    onCancel,
   } = props;

  const [isUnsavedChanges, setIsUnsavedChanges] = useState(false);
  const [selectedPolicyTemplates, setSelectedPolicyTemplates] = useState([]);
  const [policyFormArguments, setPolicyFormArguments] = useState([]);

  useEffect(() => {
    const selectedTemplates = [];
    if (props.policyTemplates.length > 0 && props.apiDetails.policyEntities
      && props.apiDetails.policyEntities.length > 0) {
      const policyTemplates = props.policyTemplates || [];
      const policyEntities = [];
      const getApiPolicyTemplateArguments = (policyTemplateArguments) =>
        policyTemplateArguments.reduce((acc, { name, value }) => {
          acc[name] = value;
          return acc;
        }, {});
      forEach(props.apiDetails.policyEntities, ({ policyEntityUuid, policyTemplateArguments }) => {
        const entity = {};
        entity[policyEntityUuid] = getApiPolicyTemplateArguments(policyTemplateArguments);
        policyEntities.push(entity);
      });
      forEach(policyEntities, (policyEntity) => {
        const selectedEntities = policyTemplates
          .filter(({ uuid }) => policyEntity[uuid])
          .map(policyTemplate => ({
            ...policyTemplate,
            arguments: get(policyTemplate, 'arguments', [])
              .map(argument => ({
                ...argument,
                value: policyEntity[policyTemplate.uuid][argument.name],
              })),
          }));
        // Adding to the rendered list of Policy Templates
        // only when the reference implementation exists
        if (selectedEntities.length > 0) { selectedTemplates.push(selectedEntities[0]); }
      });
      map(selectedTemplates, (policyTemplate, index) => {
        policyFormArguments[index] = policyFormArguments[index] || [];
        map(get(policyTemplate, 'arguments', []), (arg) => {
          if (arg.value) {
            const argObj = policyFormArguments[index].find(obj => obj.name === arg.name);
            if (!isObject(argObj)) {
              policyFormArguments[index].push({ name: arg.name, value: arg.value });
            }
          }
        });
      });
      setSelectedPolicyTemplates([...selectedTemplates]);
      setPolicyFormArguments([...policyFormArguments]);
    }
  }, [props.apiDetails, props.policyTemplates]);

  const onSelectedPolicyTemplates = (obj) => {
    selectedPolicyTemplates.push(obj);
    setSelectedPolicyTemplates([...selectedPolicyTemplates]);
  };

  const onDeletePolicyTemplate = (template, i) => {
    selectedPolicyTemplates.splice(i, 1);
    policyFormArguments.splice(i, 1);
    setSelectedPolicyTemplates([...selectedPolicyTemplates]);
    setPolicyFormArguments([...policyFormArguments]);
  };

  const onMoveUpPolicyTemplate = (template, i) => {
    if (i > 0) {
      const moveUpPolicyTemplates = selectedPolicyTemplates.splice(i, 1);
      selectedPolicyTemplates.splice(i - 1, 0, moveUpPolicyTemplates[0]);
      const moveUpPolicyFormArguments = policyFormArguments.splice(i, 1);
      policyFormArguments.splice(i - 1, 0, moveUpPolicyFormArguments[0]);
    }
    setSelectedPolicyTemplates([...selectedPolicyTemplates]);
    setPolicyFormArguments([...policyFormArguments]);
    localStorage.setItem('isUnSavedChanges', true);
  };

  const onMoveDownPolicyTemplate = (template, i) => {
    if (i < selectedPolicyTemplates.length - 1) {
      const moveDownPolicyTemplates = selectedPolicyTemplates.splice(i, 1);
      selectedPolicyTemplates.splice(i + 1, 0, moveDownPolicyTemplates[0]);
    }
    if (i < policyFormArguments.length - 1) {
      const moveDownPolicyFormArguments = policyFormArguments.splice(i, 1);
      policyFormArguments.splice(i + 1, 0, moveDownPolicyFormArguments[0]);
    }
    setSelectedPolicyTemplates([...selectedPolicyTemplates]);
    setPolicyFormArguments([...policyFormArguments]);
    localStorage.setItem('isUnSavedChanges', true);
  };

  const onChangePolicyTemplateForm = (val, name, index) => {
    policyFormArguments[index] = policyFormArguments[index] || [];
    const objectIndex = findIndex(policyFormArguments[index], { name });
    if (objectIndex > -1) {
      policyFormArguments[index][objectIndex].value = val.toString();
    } else {
      policyFormArguments[index].push({ name, value: val.toString() });
    }

    const policyObjectIndex = findIndex(selectedPolicyTemplates[index].arguments, { name });
    if (policyObjectIndex > -1) {
      selectedPolicyTemplates[index].arguments[policyObjectIndex].value = val.toString();
    } else {
      selectedPolicyTemplates[index].arguments[policyObjectIndex].push(
        { name, value: val.toString() });
    }
    setSelectedPolicyTemplates([...selectedPolicyTemplates]);
    setPolicyFormArguments([...policyFormArguments]);
  };

  const onNextPolicyTemplates = async () => {
    const data = selectedPolicyTemplates.map(({ uuid }, i) => ({
      policyEntityUuid: uuid,
      policyTemplateArguments: policyFormArguments[i] || [],
    }));
    await props.saveApiPolicyEntities(apiUuid, data);
  };

  const onNext = () => {
    localStorage.setItem('isUnSavedChanges', false);
    setIsUnsavedChanges(false);
    onNextPolicyTemplates();
  };

  const onSelect = (template) => {
    localStorage.setItem('isUnSavedChanges', true);
    setIsUnsavedChanges(true);
    onSelectedPolicyTemplates(template);
  };

  const onPolicyFormChange = (val, name, index) => {
    localStorage.setItem('isUnSavedChanges', true);
    setIsUnsavedChanges(true);
    onChangePolicyTemplateForm(val, name, index);
  };

  return (
    <div id="api-policy-container">
      <h1 className={classes.pageTitle}>Policy Templates</h1>
      {errors.length > 0 &&
        <ErrorContainer errors={errors} />
      }
      <Grid container spacing={3} className={classes.gridContainer}>
        <Grid md={12} item>
          <div className={classes.expandContainer} id="selected-policy-templates-container">
            <ExpandPanelContainer
              defaultList={selectedPolicyTemplates}
              isDeleteIcon
              onDeleteItem={onDeletePolicyTemplate}
              handleChangeDynamicForm={(val, name, index) => onPolicyFormChange(val, name, index)}
              isMoveUpIcon
              onMoveUpItem={onMoveUpPolicyTemplate}
              isMoveDownIcon
              onMoveDownItem={onMoveDownPolicyTemplate}
            />
          </div>
          <div className={classes.comboBoxContainer} id="policy-templates-container">
            <FormComboCheckBox
              id="api-policy-templates"
              data={props.policyTemplates}
              onSelect={onSelect}
              selectedvalues={selectedPolicyTemplates}
              inputLabel={'Select Policy Template'}
            />
          </div>
        </Grid>
      </Grid>
      <FormActionButtons
        onNextClick={onNext}
        nextText={SAVE_AND_NEXT_TEXT}
        onCancelClick={() => onCancel(isUnsavedChanges)}
        id="visibility-form-buttons"
      />
    </div>
  );
}

PolicyTemplates.propTypes = {
  classes: PropTypes.shape({}),
  errors: PropTypes.arrayOf(PropTypes.shape({})),
  apiUuid: PropTypes.string,
  policyTemplates: PropTypes.arrayOf(PropTypes.shape({})),
  apiDetails: PropTypes.shape({}),
  onCancel: PropTypes.func,
};



// WEBPACK FOOTER //
// ./src/pages/Api/Edit/PolicyTemplates/index.js